import "@radix-ui/themes/styles.css";
import {Grid, Box, Card, Text, Button} from '@radix-ui/themes';
import { Toggle } from "radix-ui";
import { SunIcon, MoonIcon } from "@radix-ui/react-icons"
import longlogo from "../resource/longlogo.png";
import { useEffect, useState, useRef, act } from "react";
import { client } from '../utils/sanity';
import Present from "./present";

const Remote = (props) => {

    /**
     * The states (data for the whole page)
     */
    const [serviceSlides, setServiceSlides] = useState([]);
    const [presentationWindow, setPresentationWindow] = useState(null);
    const [activeSlide, setActiveSlide] = useState(0);
    const [previewSlide, setPreviewSlide] = useState(0);
    let {slides, service, refresh, isMorning, setIsMorning} = props;

    const previewRef = useRef(null);
    const [scale, setScale] = useState(1);

    /**
     * Handles opening presentation -> present.js (presentation window)
     */
    const openPresentation = () => {
        const presentation = window.open('/present', 'Presentation', 'width=800,height=600');
        setPresentationWindow(presentation);
        setIsMorning(true);
        refresh();
    };

    /**
     * Handles data refresh button, it forces new API calls to fetch data again in the mutate
     */
    const refreshPresentation = () => {
        refresh();
        sendEventToPresentation('REFRESH_DATA', activeSlide);
    }

    /**
     * Handles morning to evening switch across the app
     */
    const toggleMorningAndEvening = () => {
        setIsMorning(!isMorning);
        sendEventToPresentation('TOGGLE_MORN_EVEN', !isMorning);
        refresh();
    }

    /**
     * Handles event being sent to presentation, the signal of the remote
     * @param {*} event_type 
     * @param {*} i 
     */
    const sendEventToPresentation = (event_type, i) => {
        if (presentationWindow) {
            presentationWindow.postMessage({
                type: event_type,
                index: i
            }, '*');
            if(typeof i === "number"){
                setPreviewSlide(i+1);
                setActiveSlide(i);
            }
        }
    }

    /**
     * Handle arrow key presses to change active slide
     */
    useEffect(() => {
        const handleKeyPress = (event) => {
            if (event.key === 'ArrowUp') {
                const newSlide = Math.max(0, activeSlide - 1);
                sendEventToPresentation('CHANGE_SLIDE', newSlide);
            } else if (event.key === 'ArrowDown') {
                const newSlide = Math.min(slides.length - 1, activeSlide + 1);
                sendEventToPresentation('CHANGE_SLIDE', newSlide);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [slides.length, activeSlide, presentationWindow]);

    /**
     * When any data for page updates, update service slides list with component cards
     */
    useEffect(() => {
        setServiceSlides(
            slides.map((slide, i) => 
                <Card asChild key={"key"+i}
                    id={i === activeSlide ? "selected" : i === previewSlide ? "preview" : ""} 
                    className="card" 
                    variant="Surface" 
                    onClick={() => sendEventToPresentation('CHANGE_SLIDE', i)}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        setPreviewSlide(i);
                    }}>
                    <Text as="div" size="2" weight="bold">
                        {`${slide.name}`}
                    </Text>
                </Card>
            )
        );
    }, [slides, service, presentationWindow, activeSlide, previewSlide]);

    /**
     * When preview window has to render, it will resize the present.js
     */
    useEffect(() => {
        const updateScale = () => {
            const container = previewRef.current;
            if (!container) return;

            const border = 4; // px per side
            const { width, height } = container.getBoundingClientRect();

            const availableWidth = Math.max(0, width - border * 2);
            const availableHeight = Math.max(0, height - border * 2);

            const winW = window.innerWidth;
            const winH = window.innerHeight;

            setScale(Math.min(availableWidth / winW, availableHeight / winH, 1));
        };

        updateScale();
        const ro = new ResizeObserver(updateScale);
        ro.observe(previewRef.current);
        window.addEventListener("resize", updateScale);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", updateScale);
        };
    }, []);

    /**
     * Render function
     */
    return (
        <Grid columns="auto" gap="3" width="auto">
            <Grid className="titles" columns="auto 1fr 1fr auto" gap="2" width="auto">
                <div className="margins">
                </div>
                <div className="logo">
                    <img width="125vh" height="25vh" src={longlogo}/> 
                </div>
                <div className="button">
                    <Toggle.Root 
                        className="toggle-service" 
                        aria-label="Morning or Evening Service"
                        pressed={!isMorning}
                        onClick={toggleMorningAndEvening}
                    >
                        {
                            isMorning
                                ? [<SunIcon style={{paddingRight:"5px"}}/>, "Morning Service"]
                                : [<MoonIcon style={{paddingRight:"5px"}}/>, "Evening Service"]
                        }
                    </Toggle.Root>
                    <Button className="button-actual" onClick={refreshPresentation}>
                        <em>Refresh Data</em>
                    </Button>
                    <Button className="button-actual" onClick={openPresentation}>
                        <strong>Open Presentation</strong>
                    </Button>
                </div>
                <div className="margins">
                </div>
            </Grid>
            <Grid columns="auto 1fr 1fr auto" gap="3" width="auto">
                <div className="margins"></div>
                <div className="remote">
                    <div className="title">Presentation Slides</div>
                    <Box className="box">
                        {serviceSlides}
                    </Box>
                </div>
                <div className="preview">
                    <div className="title">Content Preview</div>
                    <Box style={{ height: "40%", width: "100%" }} ref={previewRef}>
                        <div className="strong">Live Slide:</div>
                        <div
                            className="selected-preview"
                            style={{
                                width: window.innerWidth,
                                height: window.innerHeight,
                                transform: `scale(${scale})`,
                                transformOrigin: "top left",
                            }}
                        >
                            <Present service={service} slides={slides} currentSlide={activeSlide} isPreview={true} />
                            {!presentationWindow && 
                                <div className="disable-preview">Presentation Not Started Yet</div>
                            }
                        </div>
                    </Box>
                    <Box style={{ height: "40%", width: "100%" }} ref={previewRef}>
                        <div className="strong">Preview Slide:</div>
                        <div
                            className="preview-preview"
                            style={{
                                width: window.innerWidth,
                                height: window.innerHeight,
                                transform: `scale(${scale})`,
                                transformOrigin: "top left",
                            }}
                        >
                            <Present service={service} slides={slides} currentSlide={previewSlide} isPreview={true} />
                            {!presentationWindow && 
                                <div className="disable-preview">Presentation Not Started Yet</div>
                            }
                        </div>
                    </Box>
                </div>
                <div className="margins"></div>
            </Grid>
        </Grid>
    );
}

export default Remote;